import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Key, Droplet, Zap, AlertTriangle, Camera, MessageSquare, Plus,
  Loader2, X, ExternalLink, AlertCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

/**
 * FINT Property Notes Panel — used inside Inspector Order Detail.
 * Renders ONLY for Internal/FINT orders. Always shown (even empty).
 *
 * STRICT SCOPE: writes ONLY to property collections via dedicated
 * endpoints. Never mutates the order document.
 */

const SECTIONS = [
  {
    key: "access",
    label: "Access / Lockbox / Key",
    icon: Key,
    color: "#D1A054",
    placeholder: "e.g. Lockbox by back door, code 1234. Owner has dog.",
  },
  {
    key: "sump_pump",
    label: "Sump Pump",
    icon: Droplet,
    color: "#2F5D8A",
    placeholder: "e.g. Located in NW corner of basement, behind the boiler.",
  },
  {
    key: "electrical_panel",
    label: "Electrical Panel",
    icon: Zap,
    color: "#B08A3E",
    placeholder: "e.g. Main panel in garage, sub-panel in basement laundry.",
  },
  {
    key: "warnings",
    label: "Property Warnings / Particularities",
    icon: AlertTriangle,
    color: "#8B4545",
    placeholder: "e.g. Mold smell in basement. Cellar door cannot open. Special instructions.",
  },
];

const SUMP_PUMP_REMINDER = "⚠ Test the sump pump with a volt stick before submitting the final report.";

const PropertySection = ({ section, entries, propertyKey, onUpdated, sumpPumpValue, canWrite }) => {
  const Icon = section.icon;
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [photoCaption, setPhotoCaption] = useState("");
  const fileRef = useRef(null);
  const [lightbox, setLightbox] = useState(null); // {url, caption}

  const submitComment = async () => {
    const t = (comment || "").trim();
    if (!t) return;
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/properties/${propertyKey}/sections/${section.key}/comment`, { comment: t });
      setComment("");
      onUpdated();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const submitPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setErr("Photo exceeds 8 MB.");
      e.target.value = "";
      return;
    }
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", photoCaption || "");
      await axios.post(`${API}/properties/${propertyKey}/sections/${section.key}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotoCaption("");
      e.target.value = "";
      onUpdated();
    } catch (er) {
      setErr(er?.response?.data?.detail || er.message);
    } finally { setBusy(false); }
  };

  const setSumpPump = async (val) => {
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/properties/${propertyKey}/sections/sump_pump/status`, { value: val });
      onUpdated();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <div className="border border-gray-200 rounded-lg bg-white" data-testid={`prop-section-${section.key}`}>
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between" style={{ background: `${section.color}10` }}>
        <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: section.color }}>
          <Icon className="h-4 w-4" /> {section.label}
        </h3>
        <span className="text-[10px] text-gray-400" data-testid={`prop-section-count-${section.key}`}>
          {entries.length} entr{entries.length === 1 ? "y" : "ies"}
        </span>
      </div>

      {section.key === "sump_pump" && (
        <div className="px-4 pt-3">
          <div className="flex items-center gap-2 mb-2 text-[11px] text-[#B08A3E] bg-[#B08A3E]/10 border border-[#B08A3E]/30 rounded px-2 py-1.5" data-testid="sump-reminder">
            <AlertCircle className="h-3.5 w-3.5" /> {SUMP_PUMP_REMINDER}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase font-bold text-gray-500">Sump pump present:</span>
            {["yes", "no", "unknown"].map(v => {
              const active = (sumpPumpValue || "unknown") === v;
              return (
                <button
                  key={v}
                  onClick={() => canWrite && setSumpPump(v)}
                  disabled={!canWrite || busy}
                  className={`px-3 py-1 rounded-full text-[11px] font-bold border transition-colors ${
                    active
                      ? v === "yes" ? "bg-[#6B8F62] text-white border-[#6B8F62]"
                        : v === "no" ? "bg-[#8B4545] text-white border-[#8B4545]"
                        : "bg-gray-200 text-gray-700 border-gray-300"
                      : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                  } disabled:opacity-50`}
                  data-testid={`sump-pump-${v}`}
                >
                  {v.toUpperCase()}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Entries (newest first; comments + photos + sump status audit) */}
      <div className="p-4 space-y-2 max-h-72 overflow-y-auto" data-testid={`prop-section-feed-${section.key}`}>
        {entries.length === 0 && (
          <p className="text-[11px] text-gray-400 italic">No entries yet. Add the first one below.</p>
        )}
        {entries.map(e => (
          <div key={e.id} className="border border-gray-100 rounded p-2 text-xs" data-testid={`prop-entry-${e.id}`}>
            <div className="flex items-start justify-between gap-2 text-[10px] text-gray-500 mb-1">
              <span>{fmt(e.created_at)} · <span className="font-semibold">{e.created_by || "—"}</span> · <span className="uppercase">{e.created_by_role || ""}</span></span>
              <span className="text-[9px] uppercase tracking-wider font-bold" style={{ color: section.color }}>{e.kind === "sump_pump_status" ? "STATUS" : e.kind}</span>
            </div>
            {e.kind === "comment" && (
              <p className="text-gray-800 whitespace-pre-wrap">{e.comment_text}</p>
            )}
            {e.kind === "photo" && (
              <button
                onClick={() => setLightbox({ url: `${API}/property-photos/${e.photo_id}`, caption: e.photo_caption })}
                className="block group"
                data-testid={`prop-photo-${e.id}`}
              >
                <img
                  src={`${API}/property-photos/${e.photo_id}`}
                  alt={e.photo_caption || "property photo"}
                  className="max-h-32 rounded border border-gray-200 group-hover:border-[#2F5D8A]"
                  loading="lazy"
                />
                {e.photo_caption && <p className="text-[10px] text-gray-500 mt-1 italic">{e.photo_caption}</p>}
              </button>
            )}
            {e.kind === "sump_pump_status" && (
              <p className="text-gray-700 font-mono">→ Sump pump set to <strong>{(e.sump_pump_value || "").toUpperCase()}</strong></p>
            )}
          </div>
        ))}
      </div>

      {/* Append-only inputs */}
      {canWrite && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 space-y-2 bg-gray-50/50">
          <div>
            <textarea
              value={comment}
              onChange={ev => setComment(ev.target.value)}
              placeholder={section.placeholder}
              rows={2}
              className="w-full px-3 py-2 bg-white border border-gray-200 rounded text-xs text-[#1F2937] placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#2F5D8A]"
              data-testid={`prop-comment-input-${section.key}`}
            />
            <div className="flex items-center gap-2 mt-1">
              <button
                onClick={submitComment}
                disabled={!comment.trim() || busy}
                className="flex items-center gap-1 px-3 py-1.5 bg-[#2F5D8A] hover:bg-[#24496e] text-white text-xs font-bold rounded disabled:opacity-50"
                data-testid={`prop-comment-add-${section.key}`}>
                <MessageSquare className="h-3 w-3" /> Add comment
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={submitPhoto}
                className="hidden"
                data-testid={`prop-photo-input-${section.key}`}
              />
              <input
                value={photoCaption}
                onChange={ev => setPhotoCaption(ev.target.value)}
                placeholder="optional photo caption"
                className="flex-1 px-2 py-1.5 bg-white border border-gray-200 rounded text-[11px] text-[#1F2937] placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#2F5D8A]"
                data-testid={`prop-photo-caption-${section.key}`}
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="flex items-center gap-1 px-3 py-1.5 bg-white hover:bg-gray-50 border border-gray-300 text-[#2F5D8A] text-xs font-semibold rounded disabled:opacity-50"
                data-testid={`prop-photo-add-${section.key}`}>
                <Camera className="h-3 w-3" /> Photo
              </button>
            </div>
          </div>
          {err && <p className="text-[10px] text-[#8B4545]">{err}</p>}
          {busy && <p className="text-[10px] text-gray-400 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Saving…</p>}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4" onClick={() => setLightbox(null)} data-testid="prop-photo-lightbox">
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full" data-testid="prop-photo-lightbox-close">
            <X className="h-5 w-5" />
          </button>
          <div className="max-w-5xl max-h-[90vh] flex flex-col items-center" onClick={ev => ev.stopPropagation()}>
            <img src={lightbox.url} alt={lightbox.caption || "property photo"} className="max-w-full max-h-[80vh] rounded shadow-2xl" />
            {lightbox.caption && <p className="mt-3 text-sm text-white/90 italic">{lightbox.caption}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

const PropertyNotesPanel = ({ orderId, isInternal }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [role, setRole] = useState("");

  useEffect(() => {
    axios.get(`${API}/auth/me`)
      .then(r => setRole(r.data?.role || ""))
      .catch(() => setRole(""));
  }, []);

  const reload = useCallback(async () => {
    if (!orderId || !isInternal) return;
    setLoading(true); setErr("");
    try {
      const r = await axios.get(`${API}/orders/${orderId}/property`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [orderId, isInternal]);

  useEffect(() => { reload(); }, [reload]);

  if (!isInternal) return null;
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 flex items-center gap-2 text-gray-400 text-sm" data-testid="property-notes-panel-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading property notes…
      </div>
    );
  }
  if (err) {
    return <div className="bg-white rounded-xl border border-[#8B4545]/40 p-6 mb-6 text-[#8B4545] text-sm">Error: {err}</div>;
  }
  if (!data?.property) {
    if (data?.po_box) {
      return <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-xs text-amber-700">⚠ This order has a PO Box address — property notes are not stored for PO Boxes.</div>;
    }
    return null;
  }

  const canWrite = ["Admin", "Operations", "Operator", "Inspector"].includes(role);

  const prop = data.property;
  const sections = data.sections || {};

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6" data-testid="property-notes-panel">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="text-xs font-bold text-[#D1A054] uppercase tracking-wider flex items-center gap-2">
            <Key className="h-3.5 w-3.5" /> FINT Property Notes
          </h2>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Permanent notes for this physical address — saved across all future FINT orders.
            {prop.weak_key && <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 font-semibold text-[10px]">⚠ weak key (no zip)</span>}
          </p>
          <p className="text-[11px] text-gray-400 mt-0.5 font-mono" data-testid="property-key-display">
            {prop.address_raw}{prop.unit_norm ? ` (${prop.unit_norm})` : ""} · key={prop.property_key.substring(0, 12)}…
          </p>
        </div>
        <a
          href={`/properties/${prop.property_key}`}
          target="_blank"
          rel="noreferrer"
          className="text-[10px] text-[#2F5D8A] hover:underline font-semibold flex items-center gap-1"
          data-testid="property-deep-link">
          View full property page <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {SECTIONS.map(s => (
          <PropertySection
            key={s.key}
            section={s}
            entries={sections[s.key] || []}
            propertyKey={prop.property_key}
            sumpPumpValue={prop.sump_pump_value}
            canWrite={canWrite}
            onUpdated={reload}
          />
        ))}
      </div>

      <p className="text-[9px] text-gray-400 mt-3">
        Append-only. Old notes and photos are never deleted. Visible to Inspector / Office / Scheduling for any future order at this exact address.
      </p>
    </div>
  );
};

export default PropertyNotesPanel;
